<template>
  <div class="fixed inset-0 z-50 overflow-y-auto" v-if="isOpen">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="close"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">Записаться на прием</h2>
        </div>
        
        <div class="p-6">
          <div v-if="loading" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          </div>
          
          <div v-else-if="error" class="bg-red-50 p-4 rounded-md text-red-600 mb-4">
            {{ error }}
          </div>
          
          <div v-else>
            <!-- Doctor info -->
            <div class="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium mb-2">Информация о враче</h3>
              <p><span class="text-gray-600">ФИО:</span> {{ doctor.full_name }}</p>
              <p v-if="formattedDoctorSpecialization">
                <span class="text-gray-600">Специализация:</span> {{ formattedDoctorSpecialization }}
              </p>
              <p><span class="text-gray-600">Стаж:</span> {{ doctor.experience_years }} лет</p>
            </div>
            
            <!-- Date selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Выберите дату</label>
              <input 
                type="date" 
                v-model="selectedDate"
                class="form-input w-full rounded-md border-gray-300 shadow-sm"
                :min="minDate"
                :max="maxDate"
                @change="handleDateChange"
              >
            </div>
            
            <!-- Time slots -->
            <div class="mb-6" v-if="selectedDate && availableSlots.length > 0">
              <label class="block text-sm font-medium text-gray-700 mb-2">Доступное время</label>
              <div class="grid grid-cols-3 gap-2">
                <button 
                  v-for="slot in availableSlots" 
                  :key="slot.id"
                  class="py-2 px-3 border rounded-md text-center"
                  :class="selectedSlot && selectedSlot.id === slot.id 
                    ? 'bg-blue-100 border-blue-400 text-blue-800' 
                    : 'hover:bg-gray-50'"
                  @click="selectSlot(slot)"
                >
                  {{ formatTime(slot.start_time) }}
                </button>
              </div>
            </div>
            
            <div v-else-if="selectedDate" class="text-center py-4 text-gray-600">
              <p>На выбранную дату нет доступных слотов</p>
              <p class="mt-2 text-sm">Пожалуйста, выберите другую дату или обратитесь в клинику напрямую</p>
              <button 
                @click="handleDateChange" 
                class="mt-3 px-3 py-1 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
              >
                Попробовать загрузить ещё раз
              </button>
            </div>
            
            <!-- Selected slot details -->
            <div v-if="selectedSlot" class="mb-6 mt-2 p-3 bg-blue-50 rounded-md text-blue-800">
              <p class="font-medium">Выбранное время:</p>
              <p>{{ formatDate(selectedSlot.date) }}, {{ formatTime(selectedSlot.start_time) }} - {{ formatTime(selectedSlot.end_time) }}</p>
            </div>
            
            <!-- Reason for visit -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Причина визита <span class="text-red-500">*</span>
              </label>
              
              <!-- Загрузка причин -->
              <div v-if="loadingReasons" class="flex items-center py-2 text-gray-500">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Загрузка причин визита...
              </div>
              
              <!-- Выбор причины из списка -->
              <div v-else>
                <select 
                  v-model="selectedReasonId"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-3"
                  required
                >
                  <option value="">-- Выберите причину визита --</option>
                  <option v-for="reason in visitReasons" :key="reason.id" :value="reason.id">
                    {{ reason.name }}
                  </option>
                </select>
                
                <!-- Описание выбранной причины -->
                <div v-if="selectedReason && selectedReason.description" class="mb-3 p-2 bg-blue-50 rounded-md text-sm text-blue-700">
                  {{ selectedReason.description }}
                </div>
                
                <!-- Дополнительные детали -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Дополнительные детали (необязательно)
                  </label>
                  <textarea 
                    v-model="reasonText"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Добавьте дополнительные детали к причине визита"
                  ></textarea>
                </div>
              </div>
            </div>
            
            <!-- Success message -->
            <div v-if="bookingSuccess" class="bg-green-50 p-4 rounded-md text-green-600 mb-4">
              Вы успешно записаны на прием!
            </div>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
          <button 
            @click="close"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Закрыть
          </button>
          
          <button 
            @click="bookAppointment"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            :disabled="!canBook || loading || bookingSuccess"
            :class="{'opacity-50 cursor-not-allowed': !canBook || loading || bookingSuccess}"
          >
            {{ bookingSuccess ? 'Записано' : 'Записаться' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import { formatDoctorSpecialization } from '../utils/doctorDisplay'

export default {
  name: 'AppointmentBooking',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    doctor: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedDate: null,
      selectedSlot: null,
      availableSlots: [],
      bookingSuccess: false,
      loadingReasons: false,
      selectedReasonId: null,
      reasonText: '',
      visitReasons: []
    }
  },
  computed: {
    minDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    maxDate() {
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 30); // 30 days in advance
      return maxDate.toISOString().split('T')[0];
    },
    canBook() {
      return this.selectedSlot && this.selectedReasonId;
    },
    selectedReason() {
      return this.visitReasons.find(r => r.id === this.selectedReasonId) || null;
    },
    formattedDoctorSpecialization() {
      return formatDoctorSpecialization(this.doctor)
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.reset();
        this.loadInitialSlots();
        this.loadVisitReasons();
      }
    }
  },
  methods: {
    close() {
      if (this.bookingSuccess) {
        this.$emit('appointment-created');
      }
      this.$emit('close');
    },
    reset() {
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.reason = '';
      this.error = null;
      this.bookingSuccess = false;
      this.loadingReasons = false;
      this.selectedReasonId = null;
      this.reasonText = '';
    },
    async loadInitialSlots() {
      // Set the initial date to today
      this.selectedDate = new Date().toISOString().split('T')[0];
      await this.loadAvailableSlots();
    },
    async handleDateChange() {
      this.selectedSlot = null;
      await this.loadAvailableSlots();
    },
    async loadAvailableSlots() {
      if (!this.selectedDate) return;
      
      this.loading = true;
      this.error = null;
      
      try {
        // Format date as YYYY-MM-DD for API
        const formattedDate = this.selectedDate;
        
        // First try the available endpoint with date filtering
        const response = await api.getAvailableSlots(
          this.doctor.id, 
          formattedDate, 
          formattedDate
        );
        
        
        // Check if the response has pagination (contains 'results' field)
        const slotsData = response.data.results || response.data;
        
        // Make sure we have an array before calling filter
        if (!Array.isArray(slotsData)) {
          this.availableSlots = [];
          return;
        }
        
        // Filter slots for the selected doctor and date, and that are not booked
        this.availableSlots = slotsData.filter(slot => {
          // Convert dates to YYYY-MM-DD format for comparison
          const slotDate = new Date(slot.date).toISOString().split('T')[0];
          return (
            (!this.doctor.id || slot.doctor == this.doctor.id) && 
            !slot.is_booked &&
            slotDate === formattedDate
          );
        });
        
        // If no slots found through the first method, try the by_doctor_schedule endpoint
        if (this.availableSlots.length === 0) {
          
          const altResponse = await api.getDoctorAvailableSlots(this.doctor.id);
          
          const altSlotsData = altResponse.data.results || altResponse.data;
          
          if (Array.isArray(altSlotsData)) {
            // Filter for the selected date
            this.availableSlots = altSlotsData.filter(slot => {
              const slotDate = new Date(slot.date).toISOString().split('T')[0];
              return !slot.is_booked && slotDate === formattedDate;
            });
          }
        }
        
        // Sort by start time
        this.availableSlots.sort((a, b) => {
          return a.start_time.localeCompare(b.start_time);
        });
        
        
      } catch (error) {
        this.error = 'Не удалось загрузить доступные слоты. Пожалуйста, попробуйте позже.';
      } finally {
        this.loading = false;
      }
    },
    selectSlot(slot) {
      this.selectedSlot = slot;
    },
    formatTime(timeString) {
      // Format time as HH:MM
      return timeString.substring(0, 5);
    },
    formatDate(dateString) {
      // Format date as DD.MM.YYYY
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    async bookAppointment() {
      if (!this.canBook) return;
      
      this.loading = true;
      this.error = null;
      
      try {
        
        await api.createAppointment({
          doctor: this.doctor.id,
          slot: this.selectedSlot.id,
          reason: this.selectedReasonId,
          reason_text: this.reasonText
        });
        
        this.bookingSuccess = true;
        
        setTimeout(() => {
          this.close();
        }, 2000);
        
      } catch (error) {
        if (error.response && error.response.data && error.response.data.slot) {
          this.error = 'Этот слот уже забронирован. Пожалуйста, выберите другое время.';
          await this.loadAvailableSlots(); // Refresh slots
        } else {
          this.error = 'Не удалось записаться на прием. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.loading = false;
      }
    },
    async loadVisitReasons() {
      this.loadingReasons = true;
      try {
        const response = await api.getVisitReasons();
        this.visitReasons = response.data || [];
      } catch (error) {
        this.error = 'Не удалось загрузить список причин визита. Пожалуйста, попробуйте позже.';
      } finally {
        this.loadingReasons = false;
      }
    }
  },
  created() {
    this.loadVisitReasons();
  }
}
</script>

<style scoped>
.form-input, .form-textarea {
  @apply focus:ring-blue-500 focus:border-blue-500;
}
</style> 
